import { isAfter, isBefore, startOfDay } from 'date-fns'
import React, { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import DateRangeCalendar from '@/components-latest/primitives/DateRangeCalendar'
import Input from '@/components-latest/primitives/Input'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'

import Styles from './DateRangeModal.module.scss'

type Props = {
  startDate: Date | null
  endDate: Date | null
  isOpen: boolean
  toggleIsOpen: () => void
  onApply: (startDate: Date | null, endDate: Date | null) => void
  disabled?: boolean
}

const DateRangeModal = React.memo(
  ({ startDate, endDate, isOpen, toggleIsOpen, onApply, disabled }: Props) => {
    const { t } = useTranslation()

    const [_startDate, setStartDate] = useState<Date | null>(null)
    const [_endDate, setEndDate] = useState<Date | null>(null)

    useEffect(() => {
      if (isOpen) {
        setStartDate(startDate ? startOfDay(startDate) : null)
        setEndDate(endDate ? startOfDay(endDate) : null)
      }

      return () => {
        setStartDate(null)
        setEndDate(null)
      }
    }, [isOpen, startDate, endDate])

    const onChange = (dates: [Date, Date]) => {
      const [start, end] = dates
      setStartDate(start)
      setEndDate(end)
    }

    const onApplyClick = () => {
      onApply(_startDate, _endDate)
      toggleIsOpen()
    }

    const onStartDatePickerChange = (date: Date | null) => {
      if (!date) {
        setStartDate(_endDate || null)
        return
      }
      if (_endDate && isAfter(date, _endDate)) {
        setStartDate(_endDate)
        setEndDate(date)
        return
      }
      setStartDate(date)
    }

    const onEndDatePickerChange = (date: Date | null) => {
      if (!date) {
        setEndDate(_startDate || null)
        return
      }
      if (_startDate && isBefore(date, _startDate)) {
        setStartDate(date)
        setEndDate(_startDate)
        return
      }
      if (!_startDate) {
        setStartDate(date)
        return
      }
      setEndDate(date)
    }

    return (
      <CommonModal
        className={Styles.modal}
        hideHeader
        body={
          <div className={Styles.content}>
            <DateRangeCalendar
              startDate={_startDate}
              endDate={_endDate}
              onChange={onChange}
            />
            <div className={Styles.actions}>
              <div className={Styles.datePickers}>
                <DatePicker
                  calendarClassName={Styles.displayNone}
                  selected={_startDate}
                  dateFormat={['dd/MM/yyyy', 'dd.MM.yyyy']}
                  placeholderText="dd/mm/yyyy"
                  onChange={onStartDatePickerChange}
                  customInput={
                    <Input
                      data-testid="date-range-modal-start-date"
                      className={Styles.input}
                    />
                  }
                />
                <DatePicker
                  calendarClassName={Styles.displayNone}
                  selected={_endDate}
                  placeholderText="dd/mm/yyyy"
                  dateFormat={['dd/MM/yyyy', 'dd.MM.yyyy']}
                  onChange={onEndDatePickerChange}
                  customInput={
                    <Input
                      data-testid="date-range-modal-end-date"
                      className={Styles.input}
                    />
                  }
                />
              </div>
              <div className={Styles.buttons}>
                <Button onClick={toggleIsOpen} size="medium" variant="tertiary">
                  {t('performance.custom-range.cancel')}
                </Button>
                <Button
                  disabled={disabled || !_startDate || !_endDate}
                  onClick={onApplyClick}
                  variant="branded">
                  {t('performance.custom-range.apply')}
                </Button>
              </div>
            </div>
          </div>
        }
        open={isOpen}
        onOpenChange={toggleIsOpen}
      />
    )
  }
)

DateRangeModal.displayName = 'DateRangeModal'

export default DateRangeModal
